import { BRANCHES } from "@/pages/buildApplyV2/roleBranches";
import roleBranchesData from "@/constants/roleBranches.json";

// Freelancer Profile Data Audit (2026-02, Phase 3) — shared between the freelancer's own
// self-edit form (FreelancerPortal.jsx) and anywhere else that needs to know "which
// role-specific niche/service questions apply to this Primary/Secondary Role combo". Moved
// here (out of FreelancerPortal.jsx, where it was previously defined but never actually
// wired up) so it's a single source of truth, not duplicated per page.

// Some of the ~74 pre-v2 members still carry the OLDER role label (the v2 canonical rename
// kept these alive everywhere per the founder's "safe add-only" plan, see roleBranches.js's
// own comment) — this resolves a legacy label to its v2 BRANCHES key purely so self-edit/
// display can show the right specialisation questions; it never rewrites the freelancer's
// own Primary/Secondary Role value.
export const LEGACY_ROLE_ALIAS = roleBranchesData.LEGACY_ROLE_ALIAS;
export const branchFieldsFor = (role) => BRANCHES[role] || BRANCHES[LEGACY_ROLE_ALIAS[role]] || [];

// Role-specific specialisation questions (niches/services/etc) for a given Primary + Secondary
// Role combo, deduped by field key (first occurrence wins the label — a field like "gear"
// appears under multiple branches with slightly different wording, harmless). "gear"/
// "editing_software" are dropped since those are always-available fallback fields rendered
// separately regardless of role (see EDITABLE_FIELDS in FreelancerPortal.jsx).
export function specialisationFieldsFor(primaryRole, secondaryRoles) {
  const roles = [primaryRole, ...(secondaryRoles || [])].filter(Boolean);
  const seen = new Map();
  roles.forEach((role) => branchFieldsFor(role).forEach((b) => {
    if (!seen.has(b.key)) {
      seen.set(b.key, { key: b.key, label: b.label, optionsKey: b.optionsKey, type: b.kind === "multi" ? "multiselect" : b.kind === "single" ? "select" : "text" });
    }
  }));
  seen.delete("gear");
  seen.delete("editing_software");
  return [...seen.values()];
}

// Display-side label lookup (key -> human label) for rendering the backend's `specialisation.
// role_specific` composite (see modules/build.py's `_specialisation()`) — built once from the
// SAME BRANCHES catalog the edit form's questions come from, so a rendered label never drifts
// from what the edit form itself calls that field.
export const SPECIALISATION_LABELS = (() => {
  const map = {
    gear: "Gear", editing_software: "Editing / Post-Production Software", ai_creator_software: "AI Software",
    preferred_project_types: "Preferred Project Types", content_360_camera: "360 Camera",
  };
  Object.values(BRANCHES).forEach((fields) => fields.forEach((f) => { if (!map[f.key]) map[f.key] = f.label; }));
  return map;
})();

// Profile Completeness Banner (2026-02) — role-aware: the denominator only counts fields
// relevant to the member's OWN discipline (e.g. a Voice Over Artist's role branch has no
// gear/editing_software/ai_creator_software field at all, so the "gear/software" check is
// skipped entirely for them rather than counted as missing). Purely a display computation —
// no backend round trip needed, `f` is whatever `/my-profile` already returned.
const _GEAR_SOFTWARE_KEYS = ["gear", "editing_software", "ai_creator_software"];

function _roleNeedsGearOrSoftware(role) {
  return branchFieldsFor(role).some((b) => _GEAR_SOFTWARE_KEYS.includes(b.key));
}

export function computeProfileCompleteness(f, workSamples) {
  if (!f) return { percent: 100, missing: [], total: 0, doneCount: 0 };
  const checks = [];

  checks.push({ key: "bio", label: "Add a short bio", done: Boolean((f.bio || "").trim()) });

  const hasLink = ["website", "instagram", "youtube", "vimeo", "behance"].some((k) => f[k]) || Boolean((f.work_links || "").trim());
  checks.push({ key: "portfolio_link", label: "Add a portfolio or work link", done: hasLink });

  const roles = [f.primary_role, ...(f.secondary_roles || [])].filter(Boolean);
  if (roles.some(_roleNeedsGearOrSoftware)) {
    const hasGear = (f.gear || []).length > 0 || (f.editing_software || []).length > 0 || (f.ai_creator_software || []).length > 0;
    checks.push({ key: "gear_software", label: "Add your gear or software", done: hasGear });
  }

  const roleSpecificFields = specialisationFieldsFor(f.primary_role, f.secondary_roles);
  if (roleSpecificFields.length > 0) {
    const hasNicheOrService = roleSpecificFields.some(({ key }) => {
      const v = f[key];
      return Array.isArray(v) ? v.length > 0 : Boolean(v);
    });
    checks.push({ key: "role_specific", label: "Add your niches / services", done: hasNicheOrService });
  }

  checks.push({ key: "availability", label: "Set your availability", done: Boolean(f.availability) });
  checks.push({ key: "location", label: "Add your location", done: Boolean(f.canonical_location || f.city) });

  // Creative Discovery UX Refinement, Phase 4 (2026-08): only scored once `workSamples` has
  // actually loaded (an array) — `undefined` while `/my-work-samples` is still in flight, so a
  // fresh page load never briefly under-reports completeness before that second fetch resolves.
  if (Array.isArray(workSamples)) {
    const hasSample = workSamples.length > 0;
    checks.push({ key: "work_sample", label: "Add at least one work sample", done: hasSample });
    const hasTaggedSample = workSamples.some((s) => s.role_tag || (s.category_tags || []).length > 0);
    checks.push({ key: "work_sample_tags", label: "Tag your work", done: hasSample && hasTaggedSample });
  }

  const doneCount = checks.filter((c) => c.done).length;
  const percent = checks.length ? Math.round((doneCount / checks.length) * 100) : 100;
  return { percent, missing: checks.filter((c) => !c.done).slice(0, 2), total: checks.length, doneCount };
}
